import {
    ShopCartUrlContext,
    bulkAddToCartUrlMagento,
    getShopShareCartMergeUrlMagento
} from "../../../../common/utils/cart-util";
import { getCartErpAccountNumber } from "../store/shop-cart.selectors";
import store from "../../../../store/store";
import { getResponseData } from "../../../../common/utils/api-utils";
import environment, { CheckoutFlow } from "../../../../common/environment";
import { ShopApi, cudCartMagento, guestCartMagento, postGetGuestCartTokenMagento } from "../../../../common/constants/shop-api";
import { getCustomerToken } from "../../../customer/customer";
import { StoreState } from "../../../../store/store.interface";
import { GuestCheckoutAPI } from "../../../../common/constants/guest-checkout-api";
import {
  AddProductMagentoResponse,
  BulkAddToCartResponse,
  CustomPriceMagentoRequest,
  CustomPriceMagentoResponse,
  CustomPriceRequest,
  ShopCart
} from "../interfaces/shop-cart.interface";
import { PurchaseOrderDetails } from "../../../checkout/shop-checkout.interface";
import { getAuthenticationHeaders } from "../../../../common/utils/auth-utils";

export async function getShareCartMergeApiCallMagento(state: StoreState, shopCartUrlContext: ShopCartUrlContext){
    const token = getCustomerToken(state);
    return await getResponseData(
    new Request(environment().magentoUrl + getShopShareCartMergeUrlMagento(state), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            "shared_cart_key": shopCartUrlContext.shareCartId
        })
    }));
}

function buildIsbnPayloadForMagento(isbns: string[]): string {
  return JSON.stringify({
    cartItems: isbns.map(isbn => {
      const extensionAttribute = isbn.length > 10 ? { isbn13: isbn } : { isbn: isbn };
      return {
        extension_attributes: extensionAttribute,
        qty: 1,
      };
    })
  });
}

function buildSkuPayloadForMagento(skus: string[]): string {
  return JSON.stringify({
    cartItems: skus.map(sku => {
      return {
        sku: sku,
        qty: 1,
      };
    })
  })
}

export async function bulkAddToCartApiCallMagento(state: StoreState, isbns: string[], addItemsToMineCart: boolean): Promise<BulkAddToCartResponse[]>{
  const url = bulkAddToCartUrlMagento(state, addItemsToMineCart);
  const token = getCustomerToken(state);
  return await getResponseData(
    new Request(environment().magentoUrl + url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: addItemsToMineCart ? buildSkuPayloadForMagento(isbns) : buildIsbnPayloadForMagento(isbns),
    })
  );
}

export async function getCustomPriceCartMagentoApiCall(customStoreRequest: CustomPriceRequest): Promise<CustomPriceMagentoResponse>{
  const url = new URL(environment().magentoUrl + ShopApi.getCartsCustomPriceMagento);

  const customPriceRequest: CustomPriceMagentoRequest = {
    customPriceRequest: {
      erpCustomerId: getCartErpAccountNumber(store.getState() as StoreState),
      billToSiteId: customStoreRequest.billtoSiteId,
      shipToSiteId: customStoreRequest.shiptoSiteId
    }
  };

  return await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getCustomerToken(store.getState() as StoreState)}`
      },
      body: JSON.stringify(customPriceRequest)
    })
  );
}

export async function getGuestCartTokenMagentoApiCall(){
  const url = new URL(environment().magentoUrl + postGetGuestCartTokenMagento());
  return await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  );
}

export async function addToGuestCartMagentoApiCall(shopCartUrlContext: ShopCartUrlContext, magentoToken: string): Promise<AddProductMagentoResponse>{
  const url = new URL(environment().magentoUrl + cudCartMagento(magentoToken));
  return await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(shopCartUrlContext.addProductMagentoRequest)
    })
  );
}

export async function removeGuestCartItemsMagentoApiCall(magentoToken: string): Promise<boolean>{
  const url = new URL(environment().magentoUrl + cudCartMagento(magentoToken));
  return await getResponseData(
    new Request(url.toString(), {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })
  );
}

export async function removeLoggedInCartItemsMagentoApiCall(token: string): Promise<boolean>{
  const url = new URL(environment().magentoUrl + ShopApi.cudLoggedInCartItemsMagento);
  return await getResponseData(
    new Request(url.toString(), {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${token}`
      }
    })
  );
}

export async function updateGuestCartMagentoApiCall(itemId: string, shopCartUrlContext: ShopCartUrlContext, magentoToken: string): Promise<AddProductMagentoResponse>{
  const url = new URL(environment().magentoUrl + `${cudCartMagento(magentoToken)}/${itemId}`);
  return await getResponseData(
    new Request(url.toString(), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(shopCartUrlContext.addProductMagentoRequest)
    })
  );
}

export async function removeItemFromGuestCartMagentoApiCall(itemId: string, magentoToken: string): Promise<ShopCart>{
  const url = new URL(environment().magentoUrl + `${cudCartMagento(magentoToken)}/${itemId}`);
  return await getResponseData(
    new Request(url.toString(), {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })
  );
}

export async function addToLoggedInCartMagentoApiCall(shopCartUrlContext: ShopCartUrlContext, quoteId: string, token: string): Promise<AddProductMagentoResponse>{
  const url = new URL(environment().magentoUrl + ShopApi.addToLoggedInCartMagento(shopCartUrlContext.magentoCheckoutFlow));
  shopCartUrlContext.addProductMagentoRequest!.cartItem.quote_id = quoteId;

  return await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${token}`
      },
      body: JSON.stringify(shopCartUrlContext.addProductMagentoRequest)
    })
  );
}

export async function updateLoggedInCartMagentoApiCall(itemId: string, shopCartUrlContext: ShopCartUrlContext, quoteId: string, token: string): Promise<AddProductMagentoResponse>{
  const url = new URL(environment().magentoUrl + `${ShopApi.addToLoggedInCartMagento(shopCartUrlContext.magentoCheckoutFlow)}/${itemId}`);
  shopCartUrlContext.addProductMagentoRequest!.cartItem.quote_id = quoteId;

  return await getResponseData(
    new Request(url.toString(), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${token}`
      },
      body: JSON.stringify(shopCartUrlContext.addProductMagentoRequest)
    })
  );
}

export async function removeItemFromLoggedInCartMagentoApiCall(itemId: string, token: string): Promise<ShopCart>{
  const url = new URL(environment().magentoUrl + `${ShopApi.addToLoggedInCartMagento()}/${itemId}`);

  return await getResponseData(
    new Request(url.toString(), {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${token}`
      }
    })
  );
}

export async function getLoggedInCartQuoteIdMagentoApiCall(token: string, flow?: CheckoutFlow){
  const url = new URL(environment().magentoUrl + ShopApi.loggedInCartItemsMagento(flow));
  return await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${token}`
      },
    })
  );
}

export async function getGuestCartItemsMagentoApiCall(guestCartToken: string): Promise<ShopCart>{
  const url = new URL(environment().magentoUrl + guestCartMagento(guestCartToken));
  return await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
    })
  );
}

export async function getLoggedInCartItemsMagentoApiCall(token, requestFullCart = false){
  const path = requestFullCart ? ShopApi.loggedInCartItemsMagento() : ShopApi.loggedInCartItemsMagentoMiniCart;
  const url = new URL(environment().magentoUrl + path);
  return await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${token}`
      },
    })
  );
}

export async function mergeGuestCartMagentoApiCall(customerId: string, guestCartToken: string, loggedInUsertoken: string): Promise<boolean>{
  const url = new URL(environment().magentoUrl + guestCartMagento(guestCartToken));
  return await getResponseData(
    new Request(url.toString(), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${loggedInUsertoken}`
      },
      body: JSON.stringify({
        "customerId": customerId,
        "storeId": 1
      })
    })
  );
}

export async function getLoggedInCartItemsTotalsMagentoApiCall(token){
  const url = new URL(environment().magentoUrl + ShopApi.loggedInCartItemsTotalsMagento);
  return await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization' : `Bearer ${token}`
      },
    })
  );
}

export async function getLoggedInCartItemsTotalsMagentoApiCallGuestCheckout(cartId: string){
  const url = new URL(environment().magentoUrl + GuestCheckoutAPI.totals(cartId));
  return await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
    })
  );
}

export async function savePurchaseOrderMagentoApiCall(requestBody: PurchaseOrderDetails){
  const url = new URL(environment().magentoUrl + ShopApi.savePurchaseOrder);
  return await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: getAuthenticationHeaders(),
      body: JSON.stringify(requestBody)
    })
  );
}
